import { Controller, useForm } from "react-hook-form";
import { FormProjectData } from "types";
import React, { useCallback, useEffect, FC } from "react";
import slugify from "slugify";
import cx from "classnames";
import { ApiServiceInstance } from "services/ApiService";

import { FormBigGroup } from "modules/SubmitPage/components/FormBigGroup";
import { TextInput } from "modules/SubmitPage/components/TextInput";
import {
  validateJSON,
  validateRewardsAddress,
  validateImage,
} from "utils/validations";
import { useTokenInfo } from "modules/useTokenInfo";
import Image from "next/image";
import { getImageSrc } from "utils/getImageSrc";

import { useRouter } from "next/router";
import { useSubmitPageData } from "modules/SubmitPage/SubmitPageDataProvider";
import { getProjectUrl } from "utils/urls";
import toast from "react-hot-toast";
import * as Sentry from "@sentry/nextjs";

type Props = {};

const PreviewImageLoader = ({ url }: { url: string }) => {
  if (!url) {
    return null;
  }
  return (
    <div className="mask mask-hexagon mt-5 w-28">
      <img src={url} width={250} height={250} />
    </div>
  );
};

const ProjectDataStep: FC<Props> = () => {
  const router = useRouter();
  const {
    createProject,
    submitPageFormProjectData,
    persistProjectInfo,
    submitProjectInfo,
  } = useSubmitPageData();

  const { register, control, handleSubmit, watch, setValue, formState } =
    useForm<FormProjectData>({
      defaultValues: submitPageFormProjectData,
    });

  const tokenInfo = useTokenInfo(watch("tokenMintAddress"));

  const onSubmit = useCallback(
    async (data: FormProjectData) => {
      try {
        await createProject(data);
        await router.replace("/submit/confirmation");
      } catch (err: any) {
        Sentry.captureException(err, {
          tags: { step: "Creation" },
        });
        toast.error("There was an error creating the project", {
          duration: 5000,
          position: "bottom-center",
        });
      }
    },
    [submitProjectInfo, router]
  );

  const projectName = watch("projectName");

  useEffect(() => {
    setValue("projectSlug", slugify(projectName).toLowerCase());
  }, [setValue, projectName]);

  useEffect(() => {
    const subscription = watch((value) => persistProjectInfo(value as any));
    return () => subscription.unsubscribe();
  }, [watch, persistProjectInfo]);

  return (
    <>
      <div className="mx-auto mb-10 max-w-6xl bg-neutral p-6 shadow-2xl md:rounded-box md:p-10">
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormBigGroup
            title="Project Details"
            description="To begin, you start with the basics of your project.  You need a title, description, 
            and an image that represents your project. This sets the stage for your staking page."
            className="mb-10 md:mb-20"
            appendOnBottom={<PreviewImageLoader url={watch("imageUrl")} />}
          >
            <Controller
              name="projectName"
              control={control}
              rules={{ required: "Write your project name" }}
              render={(props) => (
                <TextInput
                  maxLength={40}
                  className="bg-neutral"
                  label="Name of the collection (max 40 char)"
                  {...props}
                />
              )}
            />

            <div className="mb-6 flex items-center">
              <div className="form-control overflow-hidden">
                <label className="input-group overflow-hidden">
                  <span className="font-mono text-xs text-gray-500">
                    https://
                  </span>
                  <input
                    type="text"
                    maxLength={180}
                    className={cx("input w-24 md:w-48", {
                      "input-error": !!formState?.errors?.projectSlug?.message,
                    })}
                    onInput={(e: any) => {
                      e.target.value = slugify(e.target.value);
                    }}
                    {...register("projectSlug", {
                      required: "Write the slug",
                      validate: async (value) => {
                        try {
                          const { data } =
                            await ApiServiceInstance.validateSlug(value);

                          return data?.isValid || "Not valid slug";
                        } catch (err) {
                          return "Not valid slug";
                        }
                      },
                    })}
                  />
                  <span className="overflow-hidden whitespace-nowrap font-mono text-xs text-gray-500">
                    .floppylabs.io
                  </span>
                </label>
              </div>
              {!!formState?.errors?.projectSlug?.message && (
                <div className="ml-4 text-sm text-red-600">
                  {formState?.errors?.projectSlug?.message}
                </div>
              )}
            </div>

            <Controller
              name="projectDescription"
              control={control}
              rules={{
                required: "Write the description of your project",
              }}
              render={(props) => (
                <TextInput
                  label="Description (max 180 char)"
                  className="h-fit w-full resize-none bg-neutral"
                  maxLength={180}
                  as="textarea"
                  {...props}
                />
              )}
            />

            <Controller
              name="imageUrl"
              control={control}
              rules={{
                required: "Write the URL for the project image",
                maxLength: 180,
                validate: async (value) => {
                  const isValid = await validateImage(value);
                  return isValid || "Provide a valid image URL";
                },
              }}
              render={(props) => (
                <TextInput
                  className="bg-neutral"
                  label="Image URL (preferably squared)"
                  maxLength={180}
                  placeholder="https://"
                  {...props}
                />
              )}
            />
          </FormBigGroup>

          <FormBigGroup
            title="Staking Config"
            description="Specify the rewards SPL token mint address. Define the amount of 
            rewards claimable daily per NFT. Set the 'Go live date' to a past date if you 
            want staking enabled right after submitting."
            className="mb-10 md:mb-20"
          >
            <div className="flex w-full items-start">
              <Controller
                name="tokenMintAddress"
                control={control}
                rules={{
                  required: "Write the rewards address",
                  validate: async (rewardAddress) =>
                    (await validateRewardsAddress(rewardAddress)) ||
                    "Not valid address",
                }}
                render={(props) => (
                  <TextInput
                    label="Rewards Token mint address"
                    className="bg-neutral font-mono"
                    groupClassName="mr-4 flex-1"
                    {...props}
                  />
                )}
              />
              <Controller
                name="dailyRewards"
                control={control}
                rules={{ required: "Write the daily rewards" }}
                render={(props) => (
                  <TextInput
                    label="Daily rewards amount / NFT"
                    groupClassName="flex-1 mb-0"
                    className="bg-neutral font-mono"
                    type="number"
                    {...props}
                  />
                )}
              />
            </div>

            {!!tokenInfo && (
              <div className="mb-6 flex items-center">
                <div className="mr-4 font-mono text-xs">
                  {watch("dailyRewards")}
                </div>
                {!!tokenInfo?.logoURI && (
                  <div className="relative mr-2 h-[18px] w-[18px]">
                    <Image
                      src={getImageSrc(tokenInfo?.logoURI)}
                      layout="fill"
                      alt={tokenInfo.name}
                    />
                  </div>
                )}
                <div className="font-mono text-xs">
                  {tokenInfo?.name} ({tokenInfo?.symbol})
                </div>
                <div className="ml-4 text-xs">per NFT per Day.</div>
              </div>
            )}

            <Controller
              name="goLiveDate"
              control={control}
              rules={{
                required: "Select the Go live",
              }}
              render={(props) => (
                <TextInput
                  label="Go live date and time"
                  type="datetime-local"
                  className="bg-neutral"
                  {...props}
                />
              )}
            />
          </FormBigGroup>

          <FormBigGroup
            title="Collection Details"
            description="Past here the Mint Hash List (Token Address List) containing all
            the NFTs of your collection. 
            It is exactly the same Hash used by secondary markets such as Magic Eden."
            className="mb-8"
          >
            <Controller
              name="hashList"
              control={control}
              rules={{
                required: "Fill the Mint Hash List",
                validate: (v) => validateJSON(v) || "Invalid Format",
              }}
              render={(props) => (
                <TextInput
                  label="Mint Hash List "
                  className={cx(
                    "h-64 w-full resize-none bg-neutral",
                    "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-500",
                    "font-mono"
                  )}
                  as="textarea"
                  placeholder='["7kSEFxkUB2DqMQ4Xa3392yPEBThTLy1Ei9t7B5yL41fD",
                    "64bcmHvviJcQrce7419DZhMr1jkCEMWy4ewbE8wE1SMF"]'
                  {...props}
                />
              )}
            />
          </FormBigGroup>

          <div className="flex justify-end">
            <button
              className={cx("btn btn-primary", {
                loading: formState?.isSubmitting,
              })}
              disabled={formState?.isSubmitting}
            >
              Continue
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export { ProjectDataStep };
